/**
 * Die Ruckel-Schalter: der Stand, den alle Wachen und Takte lesen. (02.10.2026, Ticket 2912)
 *
 * Martin, iPhone: Das Scrollen ruckelt in ALLEN Ansichten, auch in den Einstellungen. Drei
 * Reparaturen auf Verdacht haben es nicht behoben. Statt weiter zu raten, kann er am Gerät
 * einzelne Verdächtige abschalten und selbst sehen, bei welchem es flüssig wird
 * (Abschnitt „Ruckel-Test“ unter Einstellungen → Erweitert, lib/ruckel-test.ts).
 *
 * Dieses Modul ist das BLATT, nicht die Oberfläche: Es hält den Stand, schreibt ihn als
 * `data-ruckel="…"` auf `<html>` (die Wirkung steht in style.css, nichts inline), merkt ihn
 * im localStorage und sagt den Wachen, ob sie schweigen sollen. Es hängt von nichts ab außer
 * dem Protokoll-Kanal, damit scroll-wache.ts & Co. es ohne Kreis einbinden können.
 *
 * ⚠️ Ohne localStorage (Privatmodus, gesperrt) läuft alles weiter: der Stand gilt dann nur,
 *    bis die Seite neu lädt.
 * ⚠️ Das Gerät merkt sich den Stand, nicht das Konto. Wer die Schalter nicht sieht (nur
 *    Konto 1), kann sie nicht setzen — „Zurücksetzen“ leert sie.
 */
import { meldeSpur } from "./mikro-spur.js";
import { kontoGruppen } from "./konto-lage.js";

export const RUCKEL_SCHLUESSEL = "voice-v8.ruckel";

type RuckelId =
  | "animationen"
  | "filter"
  | "schatten"
  | "leisten"
  | "wachen"
  | "live"
  | "nosleep"
  | "dockblur"
  | "hintergrund"
  | "ebenen"
  | "bubblein"
  | "blasen30"
  | "folgen";

interface RuckelSchalter {
  id: RuckelId;
  /** Kurz, deutsch — so steht es am Schalter. */
  name: string;
  /** Was der Schalter abschaltet (Tooltip und Zeile darunter). */
  hinweis: string;
}

/** Reihenfolge = Nummer am Schalter: Martin sagt „bei Schalter 5 wird es flüssig“. */
export const RUCKEL_SCHALTER: readonly RuckelSchalter[] = [
  { id: "animationen", name: "Animationen aus", hinweis: "Keine Animationen und Übergänge mehr, nirgends." },
  { id: "filter", name: "Unschärfe und Filter aus", hinweis: "Keine Unschärfe hinter Leisten und Fenstern, keine Filter." },
  { id: "schatten", name: "Schatten vereinfachen", hinweis: "Keine Schatten an Kästen und Text. Farbverläufe bleiben." },
  { id: "leisten", name: "Leisten feststellen", hinweis: "Kopf- und Fußleiste ohne Mitscrollen, eigene Ebene und Unschärfe." },
  { id: "wachen", name: "Scroll-Messfühler aus", hinweis: "Alle Wachen und Beobachter, die aufs Scrollen, Wischen oder Größen schauen, ruhen." },
  { id: "live", name: "Live-Aktualisierungen pausieren", hinweis: "Uhr, Statuspunkte und Abfragen im Hintergrund warten und holen beim Ausschalten alles nach." },
  { id: "nosleep", name: "Display-Wachhalter aus (Video)", hinweis: "Das unsichtbare Wachhalte-Video steht still. Läuft die Tagesaufnahme, bleibt es an." },
  { id: "dockblur", name: "Nur Dock-Unschärfe aus", hinweis: "Nur die Unschärfe hinter der Fußleiste weg; Unschärfe sonst bleibt." },
  { id: "hintergrund", name: "Hintergrund-Ebenen aus", hinweis: "Fester Hintergrund und Körnung hinter der App weg." },
  { id: "ebenen", name: "Ebenen-Tricks aus", hinweis: "Die dauernde Grafik-Ebene je Ansicht und das Neuzeichnen beim Wechsel weg." },
  { id: "bubblein", name: "Chat: Eintrittsanimation aus", hinweis: "Neue Blasen, Karten und Hinweise im Chat erscheinen ohne Einblenden." },
  { id: "blasen30", name: "Chat: nur die letzten 30 zeigen", hinweis: "Ältere Einträge sind ausgeblendet, nichts wird gelöscht. Nur zum Testen." },
  { id: "folgen", name: "Chat: Auto-Folgen aus", hinweis: "Der Chat läuft beim Antworten nicht mehr selbst ans Ende; sanftes Scrollen wird sofort." },
];

/** `smooth` oder, bei „Chat: Auto-Folgen aus“, `auto` (Ruckel-Test, Ticket 2912). */
export function sanft(): ScrollBehavior {
  return !gesperrt && an.has("folgen") ? "auto" : "smooth";
}

const IDS: ReadonlySet<string> = new Set(RUCKEL_SCHALTER.map((s) => s.id));

function istId(x: string): x is RuckelId {
  return IDS.has(x);
}

const an = new Set<RuckelId>();

/**
 * 20.10.2026: Die Schalter sehen nur Konto 1 (Seite „Erweitert“). Ein Gerät, auf dem ein anderes Konto angemeldet ist,
 * liest sie als AUS — sonst wirkte ein einmal gesetzter Schalter weiter, und niemand könnte ihn mehr abschalten.
 * Der gemerkte Stand bleibt unberührt (kein Überschreiben); Konto 1 findet ihn wieder vor.
 */
let gesperrt = !kontoGruppen().versuche;

/** Das Konto hat sich gezeigt oder gewechselt: Sperre nachführen und Attribut neu schreiben. */
export function ruckelKontoNachfuehren(): void {
  const neu = !kontoGruppen().versuche;
  if (neu === gesperrt) return;
  gesperrt = neu;
  schreibeAttribut();
}

/** Aus dem Text im localStorage (Komma-Liste) die gültigen Namen. Unbekanntes fällt weg. */
export function ruckelAusText(text: string | null | undefined): RuckelId[] {
  if (!text) return [];
  const raus: RuckelId[] = [];
  for (const teil of text.split(",")) {
    const n = teil.trim();
    if (istId(n) && !raus.includes(n)) raus.push(n);
  }
  return raus;
}

/** Was gespeichert ist (ohne Rücksicht auf das Konto). */
function gemerkt(): RuckelId[] {
  return RUCKEL_SCHALTER.map((s) => s.id).filter((id) => an.has(id));
}

/** Was gerade wirkt: für ein Konto ohne die Seite „Erweitert“ nichts. */
function inReihenfolge(): RuckelId[] {
  return gesperrt ? [] : gemerkt();
}

/** Der Stand als Text: `animationen,filter` — oder `-`, wenn nichts an ist (für die Zeile im Protokoll). */
export function ruckelStandText(): string {
  const l = inReihenfolge();
  return l.length ? l.join(",") : "-";
}

function schreibeAttribut(): void {
  try {
    if (typeof document === "undefined") return;
    const l = inReihenfolge();
    const wurzel = document.documentElement;
    if (l.length) wurzel.setAttribute("data-ruckel", l.join(" "));
    else wurzel.removeAttribute("data-ruckel");
  } catch {
    /* nie stören */
  }
}

function merke(): void {
  try {
    const l = gemerkt();
    if (l.length) localStorage.setItem(RUCKEL_SCHLUESSEL, l.join(","));
    else localStorage.removeItem(RUCKEL_SCHLUESSEL);
  } catch {
    /* Privatmodus oder gesperrt — dann gilt der Stand nur bis zum Neuladen */
  }
}

/** Den gemerkten Stand lesen und anwenden. Beim Laden des Moduls und in Tests. */
export function ladeRuckel(): void {
  an.clear();
  try {
    for (const id of ruckelAusText(localStorage.getItem(RUCKEL_SCHLUESSEL))) an.add(id);
  } catch {
    /* kein localStorage: leer starten */
  }
  schreibeAttribut();
}

/**
 * Darf das Wachhalte-Video (NoSleep, app.ts) laufen? Nur nicht, wenn der Schalter an ist — und auch dann
 * nicht ohne Ausnahme: Läuft die Tagesaufnahme, bleibt es an, sonst legt WebKit die Audio-Sitzung still und
 * der Recorder bricht ab (Kommentar an `visibilitychange` in app.ts).
 *
 * ⚠️ 21.10.2026 (Martin 17:24: „ständige Unterbrechungen beim Vorlesen“): In der App (`nativeApp`) läuft das Video
 *    sonst NIE an. Jedes `play()` greift über WebKit in die Tonsitzung der App — iOS meldet dem Mikro-Motor
 *    „unterbrochen“ (Grund 0, „andere sitzung audio nein“), der Motor steht, Vesperas Stimme bricht ab
 *    (`spur unterbrochen began`), und der Neuanlauf der App pausiert das Video wieder. Der 30-s-Wächter warf es
 *    dann erneut an: Journal 21.10. 17:20:29 / :59 / 17:21:29 … exakt im 30-s-Takt, ebenso 16:04–16:08 (ohne
 *    Vorlesen; Ende fünf Minuten nach der letzten Berührung, wenn `displayWach` ruht). Das Display hält in der App
 *    `isIdleTimerDisabled` wach (`displayWach.app`) — das Video braucht es dort nicht. Ausnahme bleibt die
 *    Tagesaufnahme (siehe oben).
 */
export function wachhalteVideoErlaubt(tagesaufnahmeLaeuft: boolean, nativeApp = false): boolean {
  if (tagesaufnahmeLaeuft) return true;
  if (nativeApp) return false;
  return gesperrt || !an.has("nosleep");
}

/** Ist dieser Schalter an — soll die Stelle also schweigen? Eine Mengenabfrage, kostet nichts. */
export function ruckelAus(id: RuckelId): boolean {
  return !gesperrt && an.has(id);
}

export function ruckelAktive(): RuckelId[] {
  return inReihenfolge();
}

type Hoerer = (id: RuckelId, jetztAn: boolean) => void;
const hoerer = new Set<Hoerer>();

/** Wer wissen will, wenn sich ein Schalter ändert (die Oberfläche). Gibt den Abmelder zurück. */
export function beiRuckelAenderung(cb: Hoerer): () => void {
  hoerer.add(cb);
  return () => {
    hoerer.delete(cb);
  };
}

/** Aufgeschobenes je Schalter: der letzte Aufruf je Wache, beim Ausschalten genau einmal nachgeholt. */
const ausstehend = new Map<RuckelId, Map<unknown, () => void>>();

function holeNach(id: RuckelId): void {
  const liste = ausstehend.get(id);
  if (!liste) return;
  ausstehend.delete(id);
  for (const nachholen of liste.values()) {
    try {
      nachholen();
    } catch {
      /* ein Nachholer darf die anderen nicht aufhalten */
    }
  }
}

/**
 * Eine Stelle hinter einen Schalter legen. Ist er an, wird `fn` nicht aufgerufen.
 *
 * `nachholen`: Ist es eine Aktualisierung (Beobachter, Uhr, Abfrage), wird der LETZTE Aufruf
 * gemerkt und beim Ausschalten einmal nachgeholt — nichts geht verloren, es kommt nur später.
 * Bei Scroll- und Touch-Ereignissen ist Nachholen sinnlos (das nächste Ereignis bringt den
 * wahren Stand), dort bleibt es aus.
 */
export function gesteuert<A extends unknown[]>(
  id: RuckelId,
  fn: (...args: A) => void,
  nachholen = false,
): (...args: A) => void {
  const huelle = (...args: A): void => {
    if (gesperrt || !an.has(id)) {
      fn(...args);
      return;
    }
    if (!nachholen) return;
    let liste = ausstehend.get(id);
    if (!liste) {
      liste = new Map();
      ausstehend.set(id, liste);
    }
    liste.set(huelle, () => fn(...args));
  };
  return huelle;
}

/** Eine Zeile `art=schalter …` ins Protokoll — Name und Stand, nie Inhalt. */
function meldeSchalter(id: RuckelId, jetztAn: boolean, quelle: string): void {
  meldeSpur("scroll-t", `art=schalter name=${id} an=${jetztAn ? "ja" : "nein"} quelle=${quelle} stand=${ruckelStandText()}`);
}

function wechsle(id: RuckelId, soll: boolean, quelle: string): boolean {
  if (gesperrt || an.has(id) === soll) return false;
  if (soll) an.add(id);
  else an.delete(id);
  schreibeAttribut();
  merke();
  meldeSchalter(id, soll, quelle);
  for (const h of Array.from(hoerer)) {
    try {
      h(id, soll);
    } catch {
      /* eine Anzeige darf den Schalter nicht kippen */
    }
  }
  // Nachholen NACH dem Attribut: die Stelle sieht beim Nachholen schon den neuen Stand.
  if (!soll) holeNach(id);
  return true;
}

/** Einen Schalter setzen (sofort wirksam, kein Neuladen). */
export function setzeRuckel(id: RuckelId, soll: boolean): void {
  wechsle(id, soll, "einzeln");
}

/** „Alle aus“: alle Schalter an. */
export function setzeRuckelAlle(soll: boolean): void {
  for (const s of RUCKEL_SCHALTER) wechsle(s.id, soll, soll ? "alle" : "zurueck");
}

/** „Zurücksetzen“: alles wie im Alltag. */
export function ruckelZuruecksetzen(): void {
  setzeRuckelAlle(false);
}

ladeRuckel();
